// Shared primitives for the Stumble Cam filter pipeline (Plan v2 G4 — D5 addendum).
//
// Every filter in `filters/*` takes a FilterContext + (optional) opts and mutates
// the underlying canvas in place. Filters never do I/O — they're pure transforms
// so the {photoId, filter} pair renders bit-stable for goldens.
//
// The eachPixel + getImageData/putImageData helpers exist so each filter can
// stay per-pixel-pure (testable in happy-dom via a mock ctx that only needs
// getImageData/putImageData). Filters that genuinely need canvas drawing APIs
// (textOverlay, border framing) reach for ctx directly.

export type Palette = 'poster' | 'midnight' | 'faded'

export type FilterContext = {
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D
  width: number
  height: number
  rng: () => number
  palette: Palette
}

// Grain opacity per palette, matching `--grain-opacity` in
// `apps/web/src/design-canon/project/src/styles.css`.
export const GRAIN_OPACITY_BY_PALETTE: Record<Palette, number> = {
  poster: 0.18,
  midnight: 0.28,
  faded: 0.36,
}

export function clamp(n: number, lo = 0, hi = 255): number {
  if (n < lo) return lo
  if (n > hi) return hi
  return n
}

// In-place per-pixel transform. `fn` returns the new [r,g,b,a] for each pixel.
// The 5th arg `i` is the byte offset into imgData.data (multiple of 4) for
// callers that need positional info (e.g. vignette distance from center).
export function eachPixel(
  imgData: ImageData,
  fn: (r: number, g: number, b: number, a: number, i: number) => [number, number, number, number],
): void {
  const d = imgData.data
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i] ?? 0
    const g = d[i + 1] ?? 0
    const b = d[i + 2] ?? 0
    const a = d[i + 3] ?? 255
    const out = fn(r, g, b, a, i)
    d[i] = clamp(out[0])
    d[i + 1] = clamp(out[1])
    d[i + 2] = clamp(out[2])
    d[i + 3] = clamp(out[3])
  }
}

export function getImageData(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
): ImageData {
  const canvas = ctx.canvas
  return ctx.getImageData(0, 0, canvas.width, canvas.height)
}

export function putImageData(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  data: ImageData,
): void {
  ctx.putImageData(data, 0, 0)
}

// Standard Rec. 601 luma — used by saturation + textOverlay luminance probe.
export function luma(r: number, g: number, b: number): number {
  return r * 0.299 + g * 0.587 + b * 0.114
}

// Screen blend (Photoshop): result = 255 - ((255 - base) * (255 - overlay) / 255).
// Used by liftedBlacks + lightLeak.
export function screenBlend(base: number, overlay: number): number {
  return 255 - ((255 - base) * (255 - overlay)) / 255
}

// Multiply blend: result = base * overlay / 255. Used by vignette.
export function multiplyBlend(base: number, overlay: number): number {
  return (base * overlay) / 255
}
